// src/components/Translation/TranslationProvider.js
import React, { useState, useEffect, Suspense } from 'react';
import { I18nextProvider } from 'react-i18next';
import i18n from '../../i18n';
import WalletLoading from '../WalletLoading';

// Available languages from the app
const AVAILABLE_LANGUAGES = [
  'en', 'es', 'fr', 'de', 'zh', 'ja', 'ru', 'ko', 
  'ar', 'pt', 'it', 'nl', 'hi', 'tr', 'pl'
];

// Translation loading fallback component
const TranslationLoading = () => {
  const message = "Loading application...";
  return <WalletLoading message={message} minLoadTime={1000} />;
};

// Very simplified provider that just wraps the app with I18nextProvider
const TranslationProvider = ({ children }) => {
  const [isInitialized, setIsInitialized] = useState(i18n.isInitialized);
  
  useEffect(() => {
    console.log("TranslationProvider mounted, i18n status:", i18n.isInitialized);
    
    // Direct language detection and application
    const applyBrowserLanguage = () => {
      try {
        // Get browser language and normalize it
        const browserLanguage = navigator.language.split('-')[0];
        console.log("Browser language detected:", browserLanguage);
        
        // Check if the browser language is supported
        const isLanguageSupported = AVAILABLE_LANGUAGES.includes(browserLanguage);
        console.log("Is browser language supported:", isLanguageSupported);
        
        // Check if user has a stored language preference
        const hasStoredPreference = localStorage.getItem('bitdogSettings');
        let userHasSetLanguage = false;
        
        if (hasStoredPreference) {
          try {
            const settings = JSON.parse(hasStoredPreference);
            userHasSetLanguage = !!settings.language;
          } catch (e) {
            console.error("Error parsing stored settings:", e);
          }
        }
        
        console.log("User has explicit language preference:", userHasSetLanguage);
        
        // If browser language is supported and user hasn't set a preference
        if (isLanguageSupported && !userHasSetLanguage) {
          console.log("Applying browser language:", browserLanguage);
          
          // Force set the language in i18n
          i18n.changeLanguage(browserLanguage);
          
          // Also update the settings in localStorage
          if (hasStoredPreference) {
            try {
              const settings = JSON.parse(hasStoredPreference);
              settings.language = browserLanguage;
              localStorage.setItem('bitdogSettings', JSON.stringify(settings));
            } catch (e) {
              // If we can't update existing settings, create new ones
              const newSettings = { language: browserLanguage };
              localStorage.setItem('bitdogSettings', JSON.stringify(newSettings));
            }
          } else {
            // No settings exist yet, create new ones
            const newSettings = { language: browserLanguage };
            localStorage.setItem('bitdogSettings', JSON.stringify(newSettings));
          }
          
          // Add a clear visual indicator that language was changed (for debugging)
          console.log("%cLanguage set to: " + browserLanguage, "color:green; font-size:16px; font-weight:bold");
        }
      } catch (error) {
        console.error("Error in language detection:", error);
      }
    };
    
    // Apply language detection right away if i18n is initialized
    if (i18n.isInitialized) {
      applyBrowserLanguage();
    }
    
    // Handle initialization if needed
    if (!i18n.isInitialized) {
      const timeoutId = setTimeout(() => {
        console.log("Timeout reached, proceeding anyway");
        setIsInitialized(true);
        applyBrowserLanguage(); // Try again after timeout
      }, 2000);
      
      // Also set up an event listener for when i18n initializes
      const handleInitialized = () => {
        console.log("i18n initialized event fired");
        applyBrowserLanguage();
      };
      
      i18n.on('initialized', handleInitialized);
      
      return () => {
        clearTimeout(timeoutId);
        i18n.off('initialized', handleInitialized);
      };
    } else {
      setIsInitialized(true);
    }
  }, []);
  
  if (!isInitialized) {
    return <TranslationLoading />;
  }
  
  return (
    <I18nextProvider i18n={i18n}>
      <Suspense fallback={<TranslationLoading />}>
        {children}
      </Suspense>
    </I18nextProvider>
  );
};

export default TranslationProvider;